import { createTheme, Theme } from '@mui/material/styles';
import { esES } from '@mui/material/locale';
// Añade los slots de @mui/x-date-pickers (`MuiPickersOutlinedInput`…) al tipo
// `Components` del tema. Sólo tipos: no emite nada.
import type {} from '@mui/x-date-pickers/themeAugmentation';

/**
 * Cromo del layout en hex, porque MUI no parsea `oklch` (lo necesitan alpha() y
 * lighten()). Son los mismos valores que los tokens `--sidebar` y
 * `--sidebar-primary` de src/index.css, que a su vez vienen de
 * sporttia-competitions: si se toca uno hay que tocar su gemelo. El resto del
 * cromo lo pinta Tailwind directamente con los tokens, sin pasar por aquí.
 */
const sporttiaChrome = {
    sidebar: '#0f0f0f',
    /** Lima de marca (`--sidebar-primary`). */
    brand: '#baf200',
};

// Paleta de colores de Sporttia
export const sporttiaColors = {
    /**
     * Lima de marca sobre tinta. `main` es fondo, nunca texto: sobre blanco da
     * 1,3:1. Para texto e iconos sobre fondo claro está `brandText`.
     *
     * `dark` (#8bb600) es el lima para bordes decorativos y para fondos con
     * tinta encima (el día seleccionado del calendario da 7,7:1). Ojo: sobre
     * blanco mide 2,39:1, así que NO llega a los 3:1 que pide un elemento de
     * interfaz por sí solo — cualquier cosa que sea el único indicador de un
     * estado (subrayado de pestaña activa, borde del campo enfocado) va en
     * `brandText`, no aquí.
     */
    primary: {
        main: '#baf200',
        light: '#c8f861',
        dark: '#8bb600',
        contrastText: '#12150f',
    },
    /**
     * Lima de marca legible como TEXTO sobre fondo claro (6,1:1 sobre blanco).
     * Gemelo en CSS del token `--brand-text` de src/index.css: si se toca uno
     * hay que tocar el otro.
     */
    brandText: '#4f6b00',
    secondary: {
        main: '#2c3e50',
        light: '#34495e',
        dark: '#1a252f',
        contrastText: '#ffffff',
    },
    background: {
        default: '#f5f5f5',
        paper: '#ffffff',
        sidebar: sporttiaChrome.sidebar,
        sidebarHover: 'rgba(186, 242, 0, 0.08)',
        sidebarActive: 'rgba(186, 242, 0, 0.12)',
    },
    /**
     * Escala de texto del sistema de diseño: `ink` para lo principal y `dim`
     * para lo secundario. El gris anterior (#737373) se quedaba en 4,35:1
     * sobre el fondo de página — por debajo de 4,5:1 — y se lo comían los pies
     * y los metadatos. `dim` da 5,7:1 sobre página y 6,2:1 sobre blanco.
     */
    text: {
        primary: '#12150f',
        secondary: '#5c6357',
        disabled: 'rgba(0, 0, 0, 0.38)',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    /**
     * Los cuatro pares superficie/texto del sistema de diseño. `main` es el
     * tono de TEXTO del par (el «ink») y `light` su superficie (el «sf»), que
     * es como los usa MUI en `Alert` y en los chips: fondo claro y letra
     * oscura, nunca blanco sobre un color saturado.
     *
     * Los valores de MUI por defecto (#4caf50, #f44336, #ff9800, #2196f3) se
     * quedaban entre 2,7:1 y 3,7:1 con blanco encima. Y el verde de
     * confirmación es de la familia del lima a propósito: un verde genérico
     * metería un segundo acento en el sistema.
     */
    success: {
        main: '#3f5c07',
        light: '#eef6dd',
        dark: '#2f4505',
        contrastText: '#ffffff',
    },
    error: {
        main: '#c0392b',
        light: '#fdeceb',
        dark: '#9c2e22',
        contrastText: '#ffffff',
    },
    warning: {
        main: '#7a4708',
        light: '#fdf3d0',
        dark: '#5e3606',
        contrastText: '#ffffff',
    },
    info: {
        main: '#1e40af',
        light: '#e3ecfd',
        dark: '#17318a',
        contrastText: '#ffffff',
    },
    /**
     * Iconos deshabilitados. `#E0E0E0` sobre las superficies claras de la
     * aplicación daba 1,26:1 — el icono no se veía, y «deshabilitado» no
     * quiere decir invisible. `faint` del sistema da 4,8:1 y sigue leyéndose
     * como apagado frente a la tinta.
     */
    icon: {
        // Token, no hex: `#686e62` se lee en claro pero sobre las superficies
        // oscuras se queda en 2,77:1. `--muted-foreground` se aclara en modo
        // oscuro y funciona en los dos.
        disabled: 'var(--muted-foreground)',
        disabledBg: 'rgba(104, 110, 98, 0.12)',
    },
};

/**
 * Rampa de gráficas del sistema de diseño, del peldaño más oscuro al más
 * claro. Se recorre EN ORDEN y sin saltarse peldaños: para dos series, los dos
 * primeros. No se mezcla con colores de otra gama.
 *
 * Los dos primeros peldaños (6,1:1 y 3,7:1 sobre blanco) son los únicos que
 * pasan los 3:1 que pide un objeto gráfico; del tercero en adelante sólo
 * valen sobre superficie oscura.
 */
export const sporttiaChartRamp: string[] = [
    '#4f6b00',
    '#6b8e00',
    '#8bb600',
    '#a0d100',
    '#baf200',
];

// Standard UI dimensions for consistency across the application
export const sporttiaDimensions = {
    searchInput: {
        maxWidth: 300, // Standard width for search inputs with magnifying glass
    },
};

/**
 * Esquema oscuro. Los valores son los tokens `.dark` de
 * sporttia-competitions pasados a hex, para que las dos aplicaciones
 * compartan superficies (background < card) y el verde marca como acento.
 */
const darkPalette = {
    primary: {
        main: sporttiaChrome.brand,
        light: '#c8f861',
        dark: '#8bb600',
        contrastText: '#12150f',
    },
    secondary: {
        main: '#1c2f44',
        light: '#2b4a68',
        dark: '#111e2c',
        contrastText: '#afd5fe',
    },
    background: {
        default: '#171717',
        paper: '#202020',
    },
    text: {
        primary: '#f2f2f2',
        secondary: '#a4a4a4',
        disabled: 'rgba(255, 255, 255, 0.38)',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    /**
     * Los mismos cuatro pares, invertidos como los invierte el sistema en su
     * banda oscura: el «ink» se aclara y pasa a ser `main`, y la superficie
     * oscura del par queda como `dark` y como color de texto encima.
     */
    success: {
        main: '#cbe58a',
        light: '#dcefaf',
        dark: '#23300f',
        contrastText: '#23300f',
    },
    error: {
        main: '#ef8175',
        light: '#f4a49b',
        dark: '#3a1d1a',
        contrastText: '#3a1d1a',
    },
    warning: {
        main: '#f3d68a',
        light: '#f8e6b4',
        dark: '#3a2f12',
        contrastText: '#3a2f12',
    },
    info: {
        main: '#a8c3f5',
        light: '#c6d8f9',
        dark: '#16233f',
        contrastText: '#16233f',
    },
};

// Crear el tema
export const sporttiaTheme: Theme = createTheme(
    {
        // `colorSchemeSelector: 'class'` hace que MUI ponga la clase `light` o
        // `dark` en <html>, el mismo interruptor que usa la variante `dark:` de
        // Tailwind (ver @custom-variant en src/index.css).
        cssVariables: { colorSchemeSelector: 'class' },
        colorSchemes: {
            light: {
                palette: {
                    primary: sporttiaColors.primary,
                    secondary: sporttiaColors.secondary,
                    background: {
                        default: sporttiaColors.background.default,
                        paper: sporttiaColors.background.paper,
                    },
                    text: sporttiaColors.text,
                    divider: sporttiaColors.divider,
                    success: sporttiaColors.success,
                    error: sporttiaColors.error,
                    warning: sporttiaColors.warning,
                    info: sporttiaColors.info,
                },
            },
            dark: { palette: darkPalette },
        },
        typography: {
            // La misma pila que --font-sans (Inter), para que los componentes
            // de MUI y los estilados con Tailwind no mezclen tipografías.
            fontFamily: 'var(--font-sans)',
            h1: {
                fontWeight: 700,
                fontSize: '2.5rem',
                lineHeight: 1.2,
            },
            h2: {
                fontWeight: 700,
                fontSize: '2rem',
                lineHeight: 1.3,
            },
            h3: {
                fontWeight: 600,
                fontSize: '1.75rem',
                lineHeight: 1.4,
            },
            h4: {
                fontWeight: 600,
                fontSize: '1.5rem',
                lineHeight: 1.4,
            },
            h5: {
                fontWeight: 600,
                fontSize: '1.25rem',
                lineHeight: 1.5,
            },
            h6: {
                fontWeight: 600,
                fontSize: '1.125rem',
                lineHeight: 1.5,
            },
            body1: {
                fontSize: '1rem',
                lineHeight: 1.5,
            },
            body2: {
                fontSize: '0.875rem',
                lineHeight: 1.43,
            },
            button: {
                textTransform: 'none',
                fontWeight: 600,
            },
        },
        shape: {
            borderRadius: 8,
        },
        shadows: [
            'none',
            '0px 2px 4px rgba(0,0,0,0.05)',
            '0px 4px 8px rgba(0,0,0,0.08)',
            '0px 8px 16px rgba(0,0,0,0.1)',
            '0px 12px 24px rgba(0,0,0,0.12)',
            '0px 16px 32px rgba(0,0,0,0.14)',
            '0px 20px 40px rgba(0,0,0,0.16)',
            '0px 24px 48px rgba(0,0,0,0.18)',
            '0px 2px 4px rgba(0,0,0,0.05)',
            '0px 4px 8px rgba(0,0,0,0.08)',
            '0px 8px 16px rgba(0,0,0,0.1)',
            '0px 12px 24px rgba(0,0,0,0.12)',
            '0px 16px 32px rgba(0,0,0,0.14)',
            '0px 20px 40px rgba(0,0,0,0.16)',
            '0px 24px 48px rgba(0,0,0,0.18)',
            '0px 2px 4px rgba(0,0,0,0.05)',
            '0px 4px 8px rgba(0,0,0,0.08)',
            '0px 8px 16px rgba(0,0,0,0.1)',
            '0px 12px 24px rgba(0,0,0,0.12)',
            '0px 16px 32px rgba(0,0,0,0.14)',
            '0px 20px 40px rgba(0,0,0,0.16)',
            '0px 24px 48px rgba(0,0,0,0.18)',
            '0px 2px 4px rgba(0,0,0,0.05)',
            '0px 4px 8px rgba(0,0,0,0.08)',
            '0px 8px 16px rgba(0,0,0,0.1)',
        ],
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    // `CssBaseline` pone `antialiased`/`grayscale` en el body y
                    // en macOS eso adelgaza todo el texto: con la misma fuente y
                    // los mismos pesos se veía más fino que en
                    // sporttia-competitions, que deja el suavizado del sistema.
                    body: {
                        WebkitFontSmoothing: 'auto',
                        MozOsxFontSmoothing: 'auto',
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: '0px 4px 8px rgba(0,0,0,0.12)',
                        },
                    },
                    contained: {
                        '&:hover': {
                            boxShadow: '0px 4px 8px rgba(0,0,0,0.12)',
                        },
                    },
                    // Los iconos dentro de un botón sólido siguen al texto:
                    // tinta sobre lima, nunca blanco.
                    containedPrimary: ({ theme }) => ({
                        '& .MuiSvgIcon-root': {
                            color: theme.palette.primary.contrastText,
                        },
                    }),
                    // `outlined` y `text` no tienen fondo, así que MUI pintaría
                    // la etiqueta con `primary.main`: el lima de marca es color
                    // de FONDO y sobre blanco da 1,3:1. El texto va en
                    // `--brand-text` (el token, no el hex: en oscuro tiene que
                    // volver al lima) y el borde en el lima oscuro, porque una
                    // línea de 1 px en lima tampoco se ve. Se blinda aquí para
                    // que el tema haga cumplir la regla por sí solo.
                    // Los iconos no hacen falta tocarlos: sin la regla
                    // `!important` que sí tiene el botón sólido, heredan el
                    // `currentColor` de la etiqueta.
                    outlinedPrimary: {
                        color: 'var(--brand-text)',
                        borderColor: 'var(--mui-palette-primary-dark)',
                        '&:hover': {
                            borderColor: 'var(--mui-palette-primary-dark)',
                        },
                    },
                    textPrimary: {
                        color: 'var(--brand-text)',
                    },
                },
            },
            // `color="primary"` en un IconButton es `palette.primary.main`
            // directo sobre el fondo de la página: mismo problema y misma
            // solución que en los botones `outlined` y `text`.
            MuiIconButton: {
                styleOverrides: {
                    colorPrimary: {
                        color: 'var(--brand-text)',
                    },
                },
            },
            // Gemelo del anterior para los iconos sueltos: `<SchoolIcon
            // color="primary" />` no pasa por IconButton, así que se quedaba
            // en `primary.main` (1,3:1 sobre blanco). Con esta entrada quedan
            // cubiertos de una vez los `color="primary"` repartidos por la
            // aplicación, sin tocar fichero a fichero. Los iconos dentro de un
            // botón sólido siguen en tinta porque src/styles/GlobalCSS.tsx los
            // fija con `!important`.
            MuiSvgIcon: {
                styleOverrides: {
                    colorPrimary: {
                        color: 'var(--brand-text)',
                    },
                },
            },
            // La pestaña activa es el caso más repetido del lima como texto:
            // MUI pinta `Mui-selected` con `primary.main` y sobre blanco da
            // 1,3:1 (1,2:1 sobre el gris de página), así que la pestaña activa
            // se leía PEOR que las inactivas, que van en gris. La etiqueta es
            // texto, así que va al lima de texto.
            MuiTab: {
                styleOverrides: {
                    textColorPrimary: {
                        '&.Mui-selected': {
                            color: 'var(--brand-text)',
                        },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    // El subrayado se queda en el lima de marca a propósito.
                    // El sistema de diseño le da al lima el papel de RELLENO
                    // —«botones, trazos, chips activos, barras»— y su pestaña
                    // activa es exactamente `color: accent-ink` +
                    // `border-bottom-color: brand`. El estado lo carga la
                    // etiqueta, que con el lima de texto da 6,1:1; la barra
                    // sólo lo refuerza.
                    indicator: {
                        backgroundColor: 'var(--mui-palette-primary-main)',
                    },
                },
            },
            // Campo con el foco dentro: MUI tiñe la etiqueta flotante con
            // `primary.main`, de nuevo 1,3:1. La etiqueta es texto, así que va
            // al lima de texto. Cubre `TextField`, `Select` y los selectores de
            // fecha, que comparten `MuiFormLabel`.
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused': {
                            color: 'var(--brand-text)',
                        },
                    },
                },
            },
            // El recuadro de 2 px del campo enfocado, mismo origen. Va en
            // `--brand-text` y no en `primary.dark` por lo mismo que el
            // subrayado de las pestañas: es el indicador de que el campo tiene
            // el foco, necesita 3:1, y el lima oscuro se queda en 2,39:1 sobre
            // blanco. Así el borde y la etiqueta flotante comparten tono, que
            // es como se ve un campo enfocado en MUI.
            //
            // El `:not(.Mui-error)` no es decorativo: la regla de MUI que hay
            // que ganar es `.Mui-focused:not(.Mui-error) .notchedOutline`, y
            // sin repetir ese `:not()` el override tiene una clase menos de
            // especificidad y pierde aunque se declare después. Además deja
            // intacto el rojo del estado de error, que es lo que queremos.
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline':
                            {
                                borderColor: 'var(--brand-text)',
                            },
                    },
                },
            },
            // `MuiPickersOutlinedInput` es la clase propia de los selectores de
            // fecha: no hereda de `MuiOutlinedInput`, hay que nombrarla aparte.
            MuiPickersOutlinedInput: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline':
                            {
                                borderColor: 'var(--brand-text)',
                            },
                    },
                },
            },
            // Interruptor encendido. MUI pone bolita Y raíl en `primary.main`,
            // así que la bolita desaparecía dentro de su propio raíl (1,0:1) y
            // el estado sólo se leía por la posición.
            //
            // El sistema de diseño resuelve el interruptor con la bolita
            // siempre blanca y con sombra, y el cambio de estado en el RAÍL:
            // gris apagado, lima encendido. Es lo que se copia aquí — la
            // bolita blanca es una pieza con elevación, no letra sobre lima,
            // así que no cae en la regla del contraste de texto.
            MuiSwitch: {
                styleOverrides: {
                    switchBase: {
                        '&.Mui-checked': {
                            color: 'var(--mui-palette-common-white)',
                            '& + .MuiSwitch-track': {
                                backgroundColor:
                                    'var(--mui-palette-primary-main)',
                                opacity: 1,
                            },
                        },
                    },
                },
            },
            // Casilla y radio marcados. El sistema los dibuja como caja lima
            // con la marca de verificación en tinta, pero eso son dos colores
            // en una pieza y el glifo de MUI es un único trazo: con `color`
            // sólo se puede pintar la caja, y en lima puro su silueta contra el
            // blanco es de 1,3:1. Hasta que se le pase un icono propio, la
            // casilla usa el lima de texto (6,1:1), que conserva la familia y
            // se ve.
            MuiCheckbox: {
                styleOverrides: {
                    colorPrimary: {
                        '&.Mui-checked': {
                            color: 'var(--brand-text)',
                        },
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    colorPrimary: {
                        '&.Mui-checked': {
                            color: 'var(--brand-text)',
                        },
                    },
                },
            },
            // Avatar sin imagen. El gris por defecto de MUI (#bdbdbd de fondo
            // con el glifo en #f5f5f5) deja el icono en 1,72:1 y no se
            // distingue la silueta. Con el gris de texto del sistema de fondo
            // son 4,8:1.
            MuiAvatar: {
                styleOverrides: {
                    colorDefault: {
                        backgroundColor: 'var(--mui-palette-text-secondary)',
                    },
                },
            },
            MuiLink: {
                styleOverrides: {
                    // `--link` (src/index.css) y no `primary.main`: el lima de
                    // marca sobre fondo claro da 1,3:1 y un enlace no se leería.
                    root: {
                        cursor: 'pointer',
                        color: 'var(--link)',
                        textDecoration: 'none',
                        '&:hover': {
                            color: 'var(--link-hover)',
                            textDecoration: 'underline',
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                    elevation1: {
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                    },
                    elevation2: {
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundImage: 'none',
                        borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        margin: '2px 8px',
                        '&:hover': {
                            backgroundColor:
                                sporttiaColors.background.sidebarHover,
                        },
                        '&.Mui-selected': {
                            backgroundColor:
                                sporttiaColors.background.sidebarActive,
                            '&:hover': {
                                backgroundColor:
                                    sporttiaColors.background.sidebarActive,
                            },
                        },
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        fontWeight: 600,
                    },
                },
            },
            // Mismo tooltip que sporttia-competitions: chip en color de texto
            // invertido (`--foreground` sobre `--background`), 12px, radio 6.
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: 'var(--foreground)',
                        color: 'var(--background)',
                        fontSize: '0.75rem',
                        fontWeight: 400,
                        lineHeight: 1.4,
                        borderRadius: 6,
                        padding: '6px 12px',
                    },
                    arrow: {
                        color: 'var(--foreground)',
                    },
                },
            },
            // Fila de la papelera (y abonado inactivo) de SttTable y
            // SttCachedTable, que la marcan con `data-trash`. El fondo de error
            // apenas se separa de una fila normal (1,06:1 en oscuro), así que
            // lo que la distingue en los dos modos es la franja en la tinta de
            // error. El hover no cambia el fondo: la fila sigue leyéndose como
            // eliminada.
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&[data-trash], &[data-trash].MuiTableRow-hover:hover':
                            {
                                backgroundColor: 'var(--error-sf)',
                            },
                        '&[data-trash] > :first-child': {
                            boxShadow:
                                'inset 4px 0 0 var(--mui-palette-error-main)',
                        },
                    },
                },
            },
        },
    },
    esES,
);

export default sporttiaTheme;
